import { DatePipe, DOCUMENT } from "@angular/common";
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { URLS } from "../../_config/api.config";
import { ApiService } from "../../_services/api-data.service";
import { CommonService } from "../../_services/common.service";
import { ToastService } from "../../_services/toast.service";
import { ProductPreviewDetailComponent } from "../product-preview-detail/product-preview-detail.component";
import { ProductConfirmDialogComponent } from "./product-confirm-dialog.component";
import { priceGroupDateRange } from "../price-groups/price-groups.component";

@Component({
  selector: "app-products-management",
  templateUrl: "./products-management.component.html",
  styleUrls: ["./products-management.component.scss"],
})
export class ProductsManagementComponent implements OnInit, OnDestroy {
  @Input() showHeader: boolean;
  @ViewChild("filterDropdown") filterDropdown?: ElementRef<HTMLElement>;
  productListData: any[] = [];
  subClassesList: any[] = [];
  brandList: any[] = [];
  totalProductCount = 0;
  currentPage = 1;
  pagesize = 10;
  showLoader = false;
  showFilter = false;
  error = "";
  readonly skeletonRows = [0, 1, 2, 3, 4];
  readonly skeletonColumns = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  readonly filterForm = new FormGroup(
    {
      search: new FormControl(""),
      status: new FormControl(""),
      startDate: new FormControl<Date | null>(null),
      endDate: new FormControl<Date | null>(null),
      fromPrice: new FormControl<number | null>(null),
      toPrice: new FormControl<number | null>(null),
      brands: new FormControl<any[]>([]),
      subclasses: new FormControl<any[]>([]),
    },
    {
      validators: [
        priceGroupDateRange,
        (control) => {
          const { fromPrice, toPrice } = control.value;
          if (fromPrice === null && toPrice === null) return null;
          if (fromPrice === null || toPrice === null)
            return { priceRequired: true };
          return fromPrice < 0 || toPrice < 0 || fromPrice >= toPrice
            ? { priceOrder: true }
            : null;
        },
      ],
    },
  );
  appliedFilters = this.filterForm.getRawValue();
  sortKey = "";
  sortDirection: "ASC" | "DESC" | "" = "";
  private subscriptions = new Subscription();
  private listSubscription?: Subscription;
  get today() {
    return new Date();
  }
  get hasFilters() {
    const f = this.appliedFilters;
    return !!(
      f.search?.trim() ||
      f.status ||
      f.startDate ||
      f.endDate ||
      f.fromPrice !== null ||
      f.toPrice !== null ||
      f.brands?.length ||
      f.subclasses?.length
    );
  }
  constructor(
    public commonService: CommonService,
    private router: Router,
    private dataService: ApiService,
    private toastService: ToastService,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    @Inject(DOCUMENT) private document: Document,
  ) {}
  ngOnInit() {
    this.document.querySelector("#scrollId")?.scrollIntoView();
    this.loadOptions(URLS.activeSubclass, {}, false);
    this.loadOptions(
      URLS.brands,
      { page: 0, limit: 0, is_active: true, dropdown: true },
      true,
    );
    this.getProductList();
  }
  ngOnDestroy() {
    this.listSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
  }
  private loadOptions(url: string, params: any, brands: boolean) {
    this.subscriptions.add(
      this.dataService.get(url, params).subscribe({
        next: (response) => {
          if (response["code"] !== 200) return;
          const records = brands
            ? response["data"]?.result || []
            : response["data"] || [];
          const options = records.map((item: any) => ({
            id: brands ? item.brand_id : item.sub_class_id,
            name: brands
              ? this.commonService.domDirectionLTR
                ? item.brand_name_en
                : item.brand_name_ar
              : this.commonService.domDirectionLTR
                ? item.sub_class_name_en
                : item.sub_class_name_ar,
          }));
          if (brands) this.brandList = options;
          else this.subClassesList = options;
        },
        error: (error) =>
          this.toastService.displayToast(error.message, null, "danger"),
      }),
    );
  }
  getProductList() {
    this.listSubscription?.unsubscribe();
    const f = this.appliedFilters;
    const params: any = { page: this.currentPage, size: this.pagesize };
    if (f.search?.trim()) params.search = f.search.trim();
    if (f.status) params.status = f.status;
    if (f.startDate)
      params.from_date = this.datePipe.transform(f.startDate, "yyyy-MM-dd");
    if (f.endDate)
      params.to_date = this.datePipe.transform(f.endDate, "yyyy-MM-dd");
    if (f.fromPrice !== null) params.price_range_from = f.fromPrice;
    if (f.toPrice !== null) params.price_range_to = f.toPrice;
    if (f.brands?.length) params.brand_id = f.brands.join(",");
    if (f.subclasses?.length) params.sub_class_id = f.subclasses.join(",");
    if (this.sortDirection) {
      params.sort_by = this.sortKey;
      params.sort_type = this.sortDirection;
    }
    this.showLoader = true;
    this.error = "";
    this.listSubscription = this.dataService
      .get(URLS.productList, params)
      .subscribe({
        next: (response) => {
          this.showLoader = false;
          if (response["code"] === 200) {
            this.productListData = response["data"]?.result || [];
            this.totalProductCount = Number(response["data"]?.total_count) || 0;
          } else if (response["code"] === 204) {
            this.productListData = [];
            this.totalProductCount = 0;
          } else this.failed(response["message"]);
        },
        error: (error) => {
          this.showLoader = false;
          this.failed(error.message);
        },
      });
  }
  private failed(message: string) {
    this.productListData = [];
    this.totalProductCount = 0;
    this.error = message || "Unable to load products.";
  }
  openFilters() {
    if (!this.showFilter) this.filterForm.reset(this.appliedFilters);
    this.showFilter = !this.showFilter;
  }
  @HostListener("document:click", ["$event"]) closeOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (
      this.showFilter &&
      !this.filterDropdown?.nativeElement.contains(target) &&
      !target.closest(".cdk-overlay-container")
    )
      this.showFilter = false;
  }
  @HostListener("document:keydown.escape") closeFilters() {
    this.showFilter = false;
  }
  applyBtn() {
    if (this.filterForm.invalid) {
      this.filterForm.markAllAsTouched();
      return;
    }
    this.appliedFilters = this.filterForm.getRawValue();
    this.currentPage = 1;
    this.showFilter = false;
    this.getProductList();
  }
  clearFilter() {
    this.filterForm.reset({
      search: "",
      status: "",
      startDate: null,
      endDate: null,
      fromPrice: null,
      toPrice: null,
      brands: [],
      subclasses: [],
    });
    this.appliedFilters = this.filterForm.getRawValue();
    this.currentPage = 1;
    this.getProductList();
  }
  onChange(page: number) {
    if (page === this.currentPage) return;
    this.currentPage = page;
    this.getProductList();
  }
  selectPageLength(size: number) {
    this.pagesize = size;
    this.currentPage = 1;
    this.getProductList();
  }
  onSort(key: string) {
    this.sortDirection =
      this.sortKey !== key
        ? "ASC"
        : this.sortDirection === "ASC"
          ? "DESC"
          : this.sortDirection === "DESC"
            ? ""
            : "ASC";
    this.sortKey = key;
    this.currentPage = 1;
    this.getProductList();
  }
  addNewProduct() {
    this.router.navigate([
      "/account-details/products-management/product/add-new-product",
    ]);
  }
  editProductDetails(id: any) {
    this.router.navigate([
      "/account-details/products-management/product/edit/product-detail",
      id,
    ]);
  }
  viewProductDetails(id: any) {
    this.router.navigate([
      "/account-details/products-management/view-product",
      id,
    ]);
  }
  preview(id: any) {
    this.dialog.open(ProductPreviewDetailComponent, {
      width: "1000px",
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "90vh",
      data: { product_id: id },
    });
  }
  presentDeleteAlert(id: any) {
    const ref = this.dialog.open(ProductConfirmDialogComponent, {
      width: "420px",
      data: {
        header: this.commonService.domDirectionLTR
          ? "Delete product?"
          : "حذف المنتج؟",
        message: this.commonService.domDirectionLTR
          ? "This product will be removed from your catalog."
          : "سيتم إزالة هذا المنتج من الكتالوج.",
        cancel: this.commonService.domDirectionLTR ? "Cancel" : "إلغاء",
        confirm: this.commonService.domDirectionLTR ? "Delete" : "حذف",
      },
    });
    this.subscriptions.add(
      ref.afterClosed().subscribe((result) => {
        if (result === "confirm") this.removeProduct(id);
      }),
    );
  }
  removeProduct(id: any) {
    this.subscriptions.add(
      this.dataService
        .post(URLS.updateProduct, {
          product_details: [{ product_id: id, is_deleted: true }],
        })
        .subscribe({
          next: (response) => {
            if (response["code"] === 200) {
              if (this.productListData.length === 1 && this.currentPage > 1)
                this.currentPage--;
              this.getProductList();
            } else
              this.toastService.displayToast(
                response["message"],
                null,
                "danger",
              );
          },
          error: (error) =>
            this.toastService.displayToast(error.message, null, "danger"),
        }),
    );
  }
}
